import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import intl from 'react-intl-universal';
import PropTypes from 'prop-types';
import { Box, Button, Typography, Menu, MenuItem } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';

// self developed api
import api from 'utils/api';
import localData from 'utils/localData';

// Material Dashboard 2 PRO React context
import { DOLPHIN_API } from 'shared/constants/apis';
import { USER_DATA } from 'shared/constants/users';
import NotificationItem from 'components/Notifications/NotificationItem';
import {
  Content,
  TopBar,
  FilterContent,
  BottomBar,
} from 'components/Notifications/styles';
import { ServiceType } from 'shared/constants/service';
import { getNotificationLink } from './utils';
import logger from 'utils/logger';

const log = logger.createLogger('Notifications');

const Notifications = ({
  notifications,
  setNotifications,
  setNumNewNotifications,
  setOpenNotificationMenu,
  notificationFilterOptions,
}) => {
  const navigate = useNavigate();
  // State for filter menu
  const [serviceFilterAnchorEl, setServiceFilterAnchorEl] = useState(null);

  // Change this to the current user's privilege
  const [selectedServiceFilter, setSelectedServiceFilter] = useState(
    ServiceType.ALL
  );
  const [loadedAllNotifications, setLoadedAllNotifications] = useState(false);

  //! TODO The mark as all read should choose which service to mark as read
  const handleMarkAllAsRead = () => {
    const variables = {
      params: {
        organizationId: localData.get(USER_DATA.CURRENT_ORGANIZATION_ID),
        userId: localData.get(USER_DATA.USER_ID),
      },
      data: {
        type: 'MARK_ALL_CLICKED',
      },
    };
    api.put(DOLPHIN_API.NOTIFICATIONS, variables).then(async (data) => {
      if (data.message === 'ok') {
        // if succeed, update local
        const unclickedNotificationsCount = data.unclickedNotificationsCount;
        const newNotifications = [...notifications];
        newNotifications.forEach((notification) => {
          notification.clicked = true;
        });
        setNotifications([...newNotifications]);
        setNumNewNotifications(unclickedNotificationsCount);
      }
    });
  };

  const handleNotificationItemClick = (item) => {
    const variables = {
      params: {
        organizationId: localData.get(USER_DATA.CURRENT_ORGANIZATION_ID),
        userId: localData.get(USER_DATA.USER_ID),
      },
      data: {
        type: 'MARK_CLICKED',
        ids: [item.id],
      },
    };
    api.put(DOLPHIN_API.NOTIFICATIONS, variables).then(async (data) => {
      // if succeed, update local
      if (data.message === 'ok') {
        const unclickedNotificationsCount = data.unclickedNotificationsCount;
        const newNotifications = [...notifications];
        newNotifications.forEach((notification) => {
          if (notification.id === item.id) {
            notification.clicked = true;
          }
        });
        setNotifications([...newNotifications]);
        setNumNewNotifications(unclickedNotificationsCount);
      }
    });

    const link = getNotificationLink(item);
    navigate({ pathname: link });
    setOpenNotificationMenu(false);
  };

  const loadMoreNotifications = () => {
    const apiVariables = {
      params: {
        userId: localData.get(USER_DATA.USER_ID),
        organizationId: localData.get(USER_DATA.CURRENT_ORGANIZATION_ID),
        incrementFetch: true,
        timeThreshold:
          notifications.length > 0
            ? notifications[notifications.length - 1].createdAt
            : new Date().toISOString(),
      },
    };
    api.get(DOLPHIN_API.NOTIFICATIONS, apiVariables).then(async (data) => {
      if (data.notifications.length === 0) {
        setLoadedAllNotifications(true);
      } else {
        const newNotifications = [...notifications, ...data.notifications];
        setNotifications(newNotifications);
        setNumNewNotifications(data.unclickedNotificationsCount);
      }
    });
  };

  const handleServiceFilterButtonClick = (event) => {
    setServiceFilterAnchorEl(event.currentTarget);
  };

  // Close the filter menu
  const handleServiceFilterClose = () => {
    setServiceFilterAnchorEl(null);
  };

  // Handle filter selection
  const handleServiceFilterSelect = (filter) => {
    setSelectedServiceFilter(filter);
    handleServiceFilterClose();
  };

  // Filter the notifications array based on the selected service and type filters
  const filteredNotifications = notifications.filter((n) => {
    const serviceFilterMatch =
      selectedServiceFilter === ServiceType.ALL ||
      n.service === selectedServiceFilter;
    return serviceFilterMatch;
  });

  return (
    <Content data-testid="notifications-box">
      <TopBar>
        {/* 1. Top bar with filter button (left) and "mark all as read" button (right) */}
        <Box>
          <Button
            startIcon={<FilterListIcon />}
            onClick={handleServiceFilterButtonClick}
            variant="text">
            {`${intl.get('notifications_topbar_text_service')}: (${intl.get(
              `notifications_service_${selectedServiceFilter}`
            )})`}
          </Button>
          <Menu
            anchorEl={serviceFilterAnchorEl}
            slotProps={{
              paper: {
                style: {
                  padding: 0,
                },
              },
            }}
            open={Boolean(serviceFilterAnchorEl)}
            onClose={handleServiceFilterClose}>
            <FilterContent>
              {Object.entries(notificationFilterOptions).map(([key, value]) => (
                <MenuItem
                  key={key}
                  onClick={() => handleServiceFilterSelect(key)}
                  disabled={!value}>
                  {intl.get(`notifications_service_${key}`)}
                </MenuItem>
              ))}
            </FilterContent>
          </Menu>
        </Box>
        {/* Mark All as Read Button */}
        <Button variant="text" onClick={handleMarkAllAsRead}>
          {intl.get('notifications_button_mark_all_as_read')}
        </Button>
      </TopBar>

      {/* 2. Content area (scrollable, max height set via the parent Box) */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {filteredNotifications.map((notification, index) => (
          <NotificationItem
            key={index}
            notification={notification}
            handleItemClick={handleNotificationItemClick}
          />
        ))}

        {/* If no notifications are available after filtering, display a message */}
        {filteredNotifications.length === 0 && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2">
              {intl.get('notifications_text_no_new_notifications')}
            </Typography>
          </Box>
        )}
      </Box>

      {loadedAllNotifications ? (
        <Box sx={{ p: 1, textAlign: 'center' }}>
          <Typography variant="subtitle3">
            {intl.get('notifications_text_all_notifications_loaded')}
          </Typography>
        </Box>
      ) : (
        <BottomBar>
          <Button
            variant="text"
            onClick={loadMoreNotifications}
            sx={{ fontSize: '14px' }}>
            {intl.get('notifications_button_load_more')}
          </Button>
        </BottomBar>
      )}
    </Content>
  );
};

Notifications.propTypes = {
  notifications: PropTypes.array.isRequired,
  setNotifications: PropTypes.func.isRequired,
  setNumNewNotifications: PropTypes.func.isRequired,
  setOpenNotificationMenu: PropTypes.func.isRequired,
  notificationFilterOptions: PropTypes.object.isRequired,
};

export default Notifications;
